import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, Button, Spinner, Alert, Badge, Label } from 'reactstrap';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { billingApi } from '../../helpers/billing_api_helper';
import { SubscriptionPlan } from '../../helpers/stripe_types';

interface StripeUpgradeProps {
  stripePromise: any;
  onSuccess: () => void;
  onCancel: () => void;
  currentPlanPriceId?: string;
  currentPlanName?: string;
}

// Payment component that uses Stripe Elements
const StripePaymentForm: React.FC<{
  selectedPlan: SubscriptionPlan;
  clientSecret: string;
  paymentIntentId: string;
  onSuccess: () => void;
  onBack: () => void;
  onCancel: () => void;
}> = ({ selectedPlan, clientSecret, paymentIntentId, onSuccess, onBack, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing?payment_success=true`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment successful! Now confirm with backend to save payment data
        try {
          await billingApi.confirmPayment(paymentIntentId, selectedPlan.name);
          onSuccess();
        } catch (confirmError) {
          console.error('Error confirming payment with backend:', confirmError);
          // Payment succeeded but data saving failed - still show success to user
          // but log the error for debugging
          onSuccess();
        }
      } else {
        setError('Payment was not successful. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Button 
          color="link" 
          onClick={onBack}
          className="p-0 text-decoration-none"
        >
          <i className="ri-arrow-left-line me-1"></i>
          Back to Plans
        </Button>
      </div>

      <div className="text-center mb-4">
        <h5>Complete Your Subscription</h5>
        <p className="text-muted">
          You selected: <strong>{selectedPlan.name}</strong> - ${selectedPlan.amount}/{selectedPlan.interval}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <Label>Payment Method</Label>
          <PaymentElement />
        </div>

        {error && (
          <Alert color="danger" className="mb-4">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        )}

        <div className="d-flex gap-2">
          <Button 
            type="submit" 
            color="primary" 
            disabled={!stripe || processing}
            className="flex-fill"
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              `Subscribe to ${selectedPlan.name}`
            )}
          </Button>
          
          <Button 
            type="button" 
            color="secondary" 
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

// Main component for plan selection and coordination
const StripeUpgrade: React.FC<StripeUpgradeProps> = ({ stripePromise, onSuccess, onCancel, currentPlanPriceId, currentPlanName }) => {
  
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'plans' | 'proration' | 'payment'>('plans');
  const [prorationData, setProrationData] = useState<any>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await billingApi.getPlans();
      setPlans(response.data?.plans || []);
    } catch (error: any) {
      console.error('Error loading plans:', error);
      setError('Failed to load subscription plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = (priceId: string) => {
    const plan = plans.find(p => p.priceId === priceId);
    
    // Don't allow selection of current plan (by priceId or name)
    const isCurrentPlanByPriceId = priceId === currentPlanPriceId;
    const isCurrentPlanByName = plan && currentPlanName && (
      (currentPlanName.toLowerCase().includes('free') && plan.amount === 0) ||
      plan.name.toLowerCase() === currentPlanName.toLowerCase()
    );
    
    if (isCurrentPlanByPriceId || isCurrentPlanByName) {
      return;
    }
    
    setSelectedPlanId(priceId);
    setSelectedPlan(plan || null);
  };

  const handleChangePlan = async () => {
    if (!selectedPlan) return;

    // Prevent duplicate clicks by checking if already processing
    if (processing || loading) {
      console.log('Already processing or loading, ignoring duplicate click');
      return;
    }

    // Check if it's a downgrade to free plan (amount = 0)
    const isDowngradeToFreePlan = selectedPlan.amount === 0 && currentPlanName && !currentPlanName.toLowerCase().includes('free');
    
    if (isDowngradeToFreePlan) {
      // Handle downgrade to Free Plan - special confirmation flow
      const confirmMessage = `Are you sure you want to downgrade to ${selectedPlan.name}?\n\nYour current ${currentPlanName} will remain active until the end of your billing period, then automatically switch to the Free Plan. You will not be charged for the next billing cycle.`;
      
      if (window.confirm(confirmMessage)) {
        try {
          setProcessing(true);
          // Process Free Plan downgrade
          await billingApi.upgradePlan(selectedPlan.priceId, selectedPlan.name);
          setProcessing(false);
          onSuccess();
        } catch (error: any) {
          setError(error.response?.data?.message || 'Failed to process downgrade to free plan. Please try again.');
          setProcessing(false);
        }
      }
      return;
    }
    
    // Check if it's a free plan (amount = 0) - for users already on free
    if (selectedPlan.amount === 0) {
      // Handle free plan - no Stripe needed
      try {
        setProcessing(true);
        // Direct plan upgrade for free plans
        await billingApi.upgradePlan(selectedPlan.priceId, selectedPlan.name);
        setProcessing(false);
        onSuccess();
      } catch (error) {
        setError('Failed to activate free plan. Please try again.');
        setProcessing(false);
      }
      return;
    }

    // Check if user is upgrading from Free Plan to paid plan
    const isUpgradeFromFreePlan = !currentPlanPriceId || 
      (currentPlanName && currentPlanName.toLowerCase().includes('free'));

    if (isUpgradeFromFreePlan) {
      // For Free Plan to paid plan: Create new subscription directly
      try {
        setLoading(true);
        setError(null);
        
        console.log('Upgrading from Free Plan to paid plan:', selectedPlan.priceId);
        
        // Create payment intent for new subscription
        const paymentResponse = await billingApi.createPaymentIntent(selectedPlan.priceId, selectedPlan.name);
        setClientSecret(paymentResponse.data.clientSecret);
        setPaymentIntentId(paymentResponse.data.paymentIntentId);
        setStep('payment');
        
      } catch (error: any) {
        console.error('Error creating payment intent for Free Plan upgrade:', error);
        setError(error.response?.data?.message || 'Failed to start subscription. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // For paid plan to paid plan: Calculate proration first
    try {
      setLoading(true);
      setError(null);
      
      console.log('Calling calculatePlanUpgrade with priceId:', selectedPlan.priceId);
      
      // Calculate proration
      const prorationResponse = await billingApi.calculatePlanUpgrade(selectedPlan.priceId);
      console.log('Proration response:', prorationResponse);
      setProrationData(prorationResponse.data);
      setStep('proration');
    } catch (error: any) {
      console.error('Error calculating proration - Full error:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      console.error('Error config:', error.config);
      
      // Check if this is an authentication issue or something else
      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.message || '';
        if (errorMessage.includes('User not found')) {
          setError('Authentication expired. Please refresh the page and try again.');
        } else {
          setError(`Authentication error: ${errorMessage}`);
        }
      } else if (error.response?.status === 403) {
        setError('Permission denied. Please contact support if this continues.');
      } else if (error.response?.status === 500) {
        const errorMessage = error.response?.data?.message || 'Internal server error';
        setError(`Server error: ${errorMessage}`);
      } else {
        setError(error.response?.data?.message || 'Failed to calculate upgrade cost. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;
    
    // Prevent duplicate clicks by checking if already processing
    if (processing) {
      console.log('Already processing, ignoring duplicate click');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      
      console.log('Starting plan change to:', selectedPlan.name, selectedPlan.priceId);
      
      // Execute the plan upgrade/downgrade
      await billingApi.upgradePlan(selectedPlan.priceId, selectedPlan.name);
      
      console.log('Plan change completed successfully');
      onSuccess();
    } catch (error: any) {
      console.error('Error changing plan - Full error:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.status === 401) {
        setError('Authentication expired. Please refresh the page and try again.');
      } else if (error.response?.status === 403) {
        setError('Permission denied. Please contact support if this continues.');
      } else if (error.response?.status === 500) {
        const errorMessage = error.response?.data?.message || 'Internal server error';
        setError(`Server error: ${errorMessage}`);
      } else {
        setError(error.response?.data?.message || 'Failed to change plan. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };


  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleBackToPlans = () => {
    setStep('plans');
    setClientSecret(null);
    setPaymentIntentId(null);
    setProrationData(null);
    setError(null);
  };

  const handleBackToProration = () => {
    setStep('proration');
    setClientSecret(null);
    setPaymentIntentId(null);
    setError(null);
  };

  if (loading && step === 'plans') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <Spinner color="primary" />
        <span className="ms-2">Loading plans...</span>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert color="danger" className="mb-4">
          <i className="ri-error-warning-line me-2"></i>
          {error}
        </Alert>
      )}

      {step === 'plans' && (
        <div>
          {/* Header */}
          <div className="text-center mb-4">
            <h5 className="mb-3">Choose Your Plan</h5>
            <p className="text-muted mb-0">Upgrade your plan to unlock more features and grow your business</p>
          </div>

          {/* Pricing Plans */}
          <Row>
            {plans.map((plan, index) => {
              // Check if this plan is the current plan by priceId or by name (for Free Plan)
              const isCurrentPlanByPriceId = plan.priceId === currentPlanPriceId;
              const isCurrentPlanByName = currentPlanName && (
                (currentPlanName.toLowerCase().includes('free') && plan.amount === 0) ||
                plan.name.toLowerCase() === currentPlanName.toLowerCase()
              );
              const isCurrentPlan = isCurrentPlanByPriceId || isCurrentPlanByName;
              const isSelected = selectedPlanId === plan.priceId;
              
              return (
                <Col lg={6} md={6} key={index} className="mb-3">
                  <Card 
                    className={`h-100 position-relative ${
                      isSelected ? 'border-primary' : ''
                    } ${isCurrentPlan ? 'border-success bg-light' : ''} ${plan.popular ? 'border-primary' : ''}`}
                  >
                    {isCurrentPlan && (
                      <div className="position-absolute top-0 start-50 translate-middle">
                        <Badge color="success" className="fs-12">
                          CURRENT PLAN
                        </Badge>
                      </div>
                    )}
                    {plan.popular && !isCurrentPlan && (
                      <div className="position-absolute top-0 start-50 translate-middle">
                        <Badge color="primary" className="fs-12">
                          RECOMMENDED
                        </Badge>
                      </div>
                    )}
                    
                    <CardBody className="p-4">
                      <div className="text-center mb-4">
                        <h5 className="fw-bold mb-1">{plan.name}</h5>
                        <div className="mb-3">
                          <h3 className={`fw-bold mb-0 ${isCurrentPlan ? 'text-success' : 'text-primary'}`}>
                            {plan.amount === 0 ? 'Free Plan' : formatPrice(plan.amount, plan.currency)}
                            {plan.amount > 0 && (
                              <small className="text-muted fs-6 fw-normal">
                                /{plan.interval}
                              </small>
                            )}
                          </h3>
                        </div>
                      </div>

                      <ul className="list-unstyled vstack gap-2 mb-4">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="d-flex align-items-center">
                            <i className="ri-check-line text-success me-2"></i>
                            <span className="text-muted">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="d-grid gap-2">
                        {isCurrentPlan ? (
                          <Button
                            color="success"
                            className="btn-success"
                            disabled
                          >
                            <i className="ri-check-line me-2"></i>
                            Current Plan
                          </Button>
                        ) : (
                          <>
                            <input
                              type="radio"
                              className="btn-check"
                              name="selectedPlan"
                              id={plan.priceId}
                              checked={isSelected}
                              onChange={() => handlePlanSelection(plan.priceId)}
                            />
                            <Label
                              className={`btn ${
                                isSelected
                                  ? 'btn-primary'
                                  : 'btn-outline-primary'
                              }`}
                              htmlFor={plan.priceId}
                            >
                              {isSelected ? 'Selected' : 'Select Plan'}
                            </Label>
                          </>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Selected Plan Summary */}
          {selectedPlan && (
            <Card className="mt-4">
              <CardBody>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h6 className="mb-1">
                      Selected Plan: {selectedPlan.name}
                    </h6>
                    <p className="text-muted mb-0">
                      {selectedPlan.amount === 0 
                        ? 'Free Plan forever' 
                        : `${formatPrice(selectedPlan.amount, selectedPlan.currency)} per ${selectedPlan.interval}`
                      }
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end">
                    <Button 
                      color="primary" 
                      onClick={handleChangePlan}
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        'Change Plan'
                      )}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          <div className="text-center mt-4">
            <Button color="link" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {step === 'proration' && prorationData && (
        <div>
          <div className="text-center mb-4">
            <h5>Plan Upgrade Summary</h5>
            <p className="text-muted">Review the charges for your plan upgrade</p>
          </div>

          <Card className="mb-4">
            <CardBody>
              <h6 className="mb-3">Upgrade Details</h6>
              <div className="d-flex justify-content-between mb-2">
                <span>Current Plan:</span>
                <strong>{prorationData.currentPlanName} (${prorationData.currentPlanAmount})</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>New Plan:</span>
                <strong>{prorationData.newPlanName} (${prorationData.newPlanAmount})</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Days Remaining in Current Period:</span>
                <span>{prorationData.daysRemaining} days</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Next Billing Date:</span>
                <span>{new Date(prorationData.nextBillingDate).toLocaleDateString()}</span>
              </div>
            </CardBody>
          </Card>

          <Card className="mb-4">
            <CardBody>
              <h6 className="mb-3">Billing Breakdown</h6>
              <div className="d-flex justify-content-between mb-2 text-success">
                <span>Credit for unused time:</span>
                <strong>-${prorationData.proration?.unusedCredit || '0.00'}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Prorated charge for new plan:</span>
                <span>${prorationData.proration?.proratedCharge || '0.00'}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between mb-2">
                {(() => {
                  const todayCharge = parseFloat(prorationData.proration?.todayCharge || '0.00');
                  const isRefund = todayCharge < 0;
                  return (
                    <>
                      <strong>{isRefund ? 'Refund today:' : 'Amount due today:'}</strong>
                      <strong className={isRefund ? "text-success" : "text-primary"}>
                        ${Math.abs(todayCharge).toFixed(2)}
                      </strong>
                    </>
                  );
                })()}
              </div>
              <div className="d-flex justify-content-between">
                <span>Next billing amount:</span>
                <span>${prorationData.proration?.nextBillingAmount || '0.00'}</span>
              </div>
            </CardBody>
          </Card>

          {prorationData.proration?.description && (
            <Alert color="info" className="mb-4">
              <small>{prorationData.proration.description}</small>
            </Alert>
          )}

          <div className="d-flex gap-2">
            <Button 
              color="primary" 
              onClick={handleConfirmUpgrade}
              disabled={processing}
              className={`flex-fill ${processing ? 'cursor-not-allowed' : ''}`}
              style={{ 
                opacity: processing ? 0.6 : 1,
                pointerEvents: processing ? 'none' : 'auto'
              }}
            >
              {processing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (() => {
                const todayCharge = parseFloat(prorationData.proration?.todayCharge || '0.00');
                const isRefund = todayCharge < 0;
                return isRefund ? 'Confirm Downgrade' : 'Confirm Upgrade';
              })()}
            </Button>
            
            <Button 
              color="secondary" 
              onClick={handleBackToPlans}
              disabled={processing}
              style={{ 
                opacity: processing ? 0.5 : 1,
                cursor: processing ? 'not-allowed' : 'pointer'
              }}
            >
              Back
            </Button>
          </div>
        </div>
      )}

      {step === 'payment' && selectedPlan && clientSecret && paymentIntentId && stripePromise && (
        <Elements 
          stripe={stripePromise} 
          options={{ 
            clientSecret,
            appearance: {
              theme: 'stripe'
            }
          }}
        >
          <StripePaymentForm
            selectedPlan={selectedPlan}
            clientSecret={clientSecret}
            paymentIntentId={paymentIntentId!}
            onSuccess={onSuccess}
            onBack={handleBackToPlans}
            onCancel={onCancel}
          />
        </Elements>
      )}

      {step === 'payment' && selectedPlan && (!clientSecret || !paymentIntentId || !stripePromise) && (
        <div className="text-center py-4">
          <Alert color="warning">
            <i className="ri-error-warning-line me-2"></i>
            Payment system is initializing. Please wait...
          </Alert>
          <Button color="secondary" onClick={handleBackToPlans}>
            Back to Plans
          </Button>
        </div>
      )}

      {loading && step === 'payment' && (
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Initializing payment...</p>
        </div>
      )}
    </div>
  );
};

export default StripeUpgrade;
